/**
 * DEFAULT ANIMAL — sticky, condensing header.
 *
 * Preserves Dawn's `sticky-header` contract (hide / reveal / reset on scroll,
 * `preventHeaderReveal` event, closing the menu and search modal on hide) and
 * layers a DEFAULT ANIMAL "condense on scroll"
 * behaviour: once the page has scrolled past a small threshold the header
 * shrinks its padding and logo via a `.header-wrapper--condensed` class.
 * Animation is transform/padding only and is disabled under reduced motion.
 */
class StickyHeader extends HTMLElement {
  #onScrollHandler = null;
  #hideHeaderOnScrollUp = null;
  #condenseThreshold = 60;

  connectedCallback() {
    this.header = document.querySelector('.section-header');
    // The `.header-wrapper` class lives on this custom element itself.
    this.headerWrapper = this.classList.contains('header-wrapper') ? this : this.querySelector('.header-wrapper');
    this.headerIsAlwaysSticky =
      this.getAttribute('data-sticky-type') === 'always' ||
      this.getAttribute('data-sticky-type') === 'reduce-logo-size';
    this.condenseOnScroll = this.#readCondenseFlag();
    this.headerBounds = {};

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add('shopify-section-header-sticky');
    }

    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector('predictive-search');

    this.#onScrollHandler = this.onScroll.bind(this);
    this.#hideHeaderOnScrollUp = () => {
      this.preventReveal = true;
    };

    this.addEventListener('preventHeaderReveal', this.#hideHeaderOnScrollUp);
    window.addEventListener('scroll', this.#onScrollHandler, false);

    this.createObserver();
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.#hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.#onScrollHandler);
  }

  #readCondenseFlag() {
    const script = document.querySelector('script[data-condense-on-scroll]');
    return script?.dataset.condenseOnScroll === 'true';
  }

  createObserver() {
    const observer = new IntersectionObserver((entries, obs) => {
      this.headerBounds = entries[0].intersectionRect;
      obs.disconnect();
    });

    observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (this.condenseOnScroll && this.headerWrapper) {
      this.headerWrapper.classList.toggle('header-wrapper--condensed', scrollTop > this.#condenseThreshold);
    }

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (this.preventHide) return;
      requestAnimationFrame(this.hide.bind(this));
    } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);

        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);

        requestAnimationFrame(this.hide.bind(this));
      }
    } else if (scrollTop <= this.headerBounds.top) {
      this.header.classList.remove('scrolled-past-header');
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
    this.closeMenuDisclosure();
    this.closeSearchModal();
  }

  reveal() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
  }

  reset() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
  }

  closeMenuDisclosure() {
    this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
    this.disclosures.forEach((disclosure) => disclosure.close());
  }

  closeSearchModal() {
    this.searchModal = this.searchModal || this.header.querySelector('details-modal');
    this.searchModal?.close(false);
  }
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}
